// Error codes from the backend (e.g. COMPANY_BILLING_INCOMPLETE).
//
// The interceptor turns every error into a string, which drops the `code` the
// backend puts next to `message` ({ status: false, code, message, data }). A
// request sent with `{ [KEEP_ERROR_CODE]: true }` in its config is rejected with
// an ApiCodeError instead when the response carries a code. Only callers that
// send the flag get the object, so every other caller still gets a string.
// apiErrorMessage() reads ApiCodeError's message like any Error.

export const KEEP_ERROR_CODE = "keepErrorCode";

export class ApiCodeError extends Error {
  code: string;
  status: number;
  data: unknown;
  /** VALIDATION_FAILED: the message for each field that failed, by field name. */
  fields: Record<string, string> | null;

  constructor(message: string, code: string, status: number, data: unknown, fields: Record<string, string> | null = null) {
    super(message);
    this.name = "ApiCodeError";
    this.code = code;
    this.status = status;
    this.data = data;
    this.fields = fields;
  }
}

/** The backend's error code, or null for anything that isn't an ApiCodeError. */
export const apiErrorCode = (err: unknown): string | null =>
  err instanceof ApiCodeError ? err.code : null;

/** For the interceptor: an ApiCodeError when the request opted in and a code came back. */
export function codeErrorFrom(error: any, message: string): ApiCodeError | null {
  const code = error?.response?.data?.code;
  if (!error?.config?.[KEEP_ERROR_CODE] || typeof code !== "string" || code === "") return null;
  const raw = error.response.data?.fields;
  const fields: Record<string, string> | null =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? Object.fromEntries(Object.entries(raw).filter(([, v]) => typeof v === "string")) as Record<string, string>
      : null;
  return new ApiCodeError(message, code, error.response.status, error.response.data?.data, fields);
}
